import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, RefreshCw, X, Users, Send, CheckCheck, Video, ShieldAlert, Edit3, Trash2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const EMPTY = {
  title: "", session_date: "", start_time: "10:00 AM", end_time: "11:30 AM",
  time_zone: "America/Los_Angeles", instructor: "", cohost: "",
  seat_limit: 25, registration_deadline: "",
  zoom_join_url: "", zoom_meeting_id: "", zoom_passcode: "",
  admin_notes: "", status: "draft", course_code: "CTA-001",
};

const COURSE_LABEL = { "CTA-001": "CTA-001 — Grief in the Workplace", "HOSP-001": "HOSP-001 — See the Person First" };
const HOSP_TITLE_PREFIX = "From the Book to the Bedside — Live · ";

const STATUS_LABEL = {
  draft: "Draft", open: "Open for Registration", closed: "Registration Closed",
  completed: "Completed", cancelled: "Cancelled",
};

const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  closed: "bg-amber-50 text-amber-800 border border-amber-200",
  completed: "bg-slate-100 text-slate-600",
  cancelled: "bg-rose-50 text-rose-700 border border-rose-200",
};

export default function AdminLiveClasses() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {mode:"create"|"edit", data}
  const [rosterFor, setRosterFor] = useState(null);
  const [rosterData, setRosterData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/portal/live-sessions");
      setSessions(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRoster = async (s) => {
    setRosterFor(s); setRosterData(null);
    try { const r = await api.get(`/admin/portal/live-sessions/${s.id}/roster`); setRosterData(r.data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/portal/live-sessions/${id}`, { status }); toast.success(`Status → ${STATUS_LABEL[status]}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const cancelSession = async (id) => {
    if (!window.confirm("Cancel this session? Learners will lose access to Zoom info. Registrations remain for audit.")) return;
    try { await api.delete(`/admin/portal/live-sessions/${id}`); toast.success("Session cancelled."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const sendReminderNow = async (id) => {
    try {
      const r = await api.post(`/admin/portal/live-sessions/${id}/send-reminder`);
      toast.success(`Reminder: ${r.data.sent} sent, ${r.data.already_sent} previously sent.`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="mt-10" data-testid="admin-live-classes">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">Live Classes</p>
          <h3 className="font-heading text-xl text-slate-900">Academy Live Classes — Instructor-led Zoom sessions</h3>
          <p className="text-sm text-slate-500 mt-0.5">Supports CTA-001 · HOSP-001 (From the Book to the Bedside — Live)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditor({ mode: "create", data: { ...EMPTY } })} data-testid="live-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B]">
            <CalendarPlus className="w-4 h-4" /> NEW LIVE CLASS
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:border-[#4A6FA5]"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-500 text-sm">Loading…</div> :
          sessions.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">No live sessions yet. Click NEW LIVE CLASS.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Title</th>
                  <th className="text-left px-4 py-3 font-semibold">Date · Time</th>
                  <th className="text-left px-4 py-3 font-semibold">Instructor</th>
                  <th className="text-left px-4 py-3 font-semibold">Seats</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sessions.map(s => (
                  <tr key={s.id} data-testid={`live-row-${s.id}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-900 font-medium">
                      {s.title}
                      <div className="mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold bg-[#F0F4F8] text-[#4A6FA5]">{s.course_code || "CTA-001"}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{s.session_date}<div className="text-slate-500">{s.start_time}–{s.end_time} {s.time_zone}</div></td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{s.instructor}</td>
                    <td className="px-4 py-3 text-xs">{s.seats_used}/{s.seat_limit}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded text-[11px] ${STATUS_COLOR[s.status]}`}>{STATUS_LABEL[s.status]}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => openRoster(s)} data-testid={`live-roster-${s.id}`} className="text-[#4A6FA5] hover:text-[#3B597B] text-xs font-medium"><Users className="w-3.5 h-3.5 inline mr-1" />Roster</button>
                        <button onClick={() => setEditor({ mode: "edit", data: s })} className="text-slate-600 hover:text-[#4A6FA5] text-xs"><Edit3 className="w-3.5 h-3.5 inline" /></button>
                        {s.status === "draft" && <button onClick={() => setStatus(s.id, "open")} className="text-emerald-700 text-xs font-semibold">Open</button>}
                        {s.status === "open" && <button onClick={() => setStatus(s.id, "closed")} className="text-amber-700 text-xs font-semibold">Close reg</button>}
                        {s.status === "closed" && <button onClick={() => setStatus(s.id, "open")} className="text-emerald-700 text-xs font-semibold">Reopen</button>}
                        {s.status !== "cancelled" && s.status !== "completed" && <button onClick={() => cancelSession(s.id)} className="text-rose-700 text-xs"><Trash2 className="w-3.5 h-3.5 inline" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>

      {editor && <EditorDrawer state={editor} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
      {rosterFor && <RosterDrawer session={rosterFor} data={rosterData} onClose={() => { setRosterFor(null); setRosterData(null); }} onSendReminder={() => sendReminderNow(rosterFor.id)} refresh={() => openRoster(rosterFor)} />}
    </div>
  );
}

function EditorDrawer({ state, onClose, onSaved }) {
  const isEdit = state.mode === "edit";
  const [f, setF] = useState(state.data);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      // Sanitize empties
      for (const k of ["cohost", "registration_deadline", "zoom_meeting_id", "zoom_passcode", "admin_notes"]) {
        if (!payload[k]) delete payload[k];
      }
      if (isEdit) await api.patch(`/admin/portal/live-sessions/${state.data.id}`, payload);
      else await api.post("/admin/portal/live-sessions", payload);
      toast.success(isEdit ? "Session updated." : "Session created.");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="live-editor">
      <div className="flex-1 bg-slate-900/40" onClick={onClose}/>
      <div className="w-full max-w-lg bg-white border-l border-slate-200 overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-slate-900">{isEdit ? "Edit live class" : "New live class"}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Course</label>
            <select
              value={f.course_code || "CTA-001"}
              onChange={e => {
                const code = e.target.value;
                setF(prev => {
                  const next = { ...prev, course_code: code };
                  const isPrefix = !prev.title || prev.title.startsWith(HOSP_TITLE_PREFIX) || prev.title === "";
                  if (code === "HOSP-001" && (!prev.title || isPrefix)) {
                    next.title = HOSP_TITLE_PREFIX + (prev.session_date || "");
                  } else if (code === "CTA-001" && prev.title.startsWith(HOSP_TITLE_PREFIX)) {
                    next.title = "";
                  }
                  return next;
                });
              }}
              disabled={isEdit}
              data-testid="live-f-course-code"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-50 disabled:text-slate-500"
            >
              {Object.entries(COURSE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {isEdit && <p className="mt-1 text-[11px] text-slate-500">Course cannot be changed after creation.</p>}
          </div>
          <Field label="Title" v={f.title} on={v=>set("title",v)} testid="live-f-title"/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Session date (YYYY-MM-DD)" v={f.session_date} on={v=>set("session_date",v)} testid="live-f-date"/>
            <Field label="Time zone" v={f.time_zone} on={v=>set("time_zone",v)} testid="live-f-tz"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start time" v={f.start_time} on={v=>set("start_time",v)}/>
            <Field label="End time" v={f.end_time} on={v=>set("end_time",v)}/>
          </div>
          <Field label="Instructor" v={f.instructor} on={v=>set("instructor",v)} testid="live-f-instructor"/>
          <Field label="Co-host / moderator" v={f.cohost || ""} on={v=>set("cohost",v)}/>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Seat limit" v={f.seat_limit} on={v=>set("seat_limit",v)} testid="live-f-seats"/>
            <Field label="Registration deadline (ISO)" v={f.registration_deadline || ""} on={v=>set("registration_deadline",v)}/>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-amber-900 font-semibold"><ShieldAlert className="w-3 h-3"/> Private — Zoom access</div>
            <Field label="Zoom join URL" v={f.zoom_join_url} on={v=>set("zoom_join_url",v)} testid="live-f-zoomurl"/>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Meeting ID" v={f.zoom_meeting_id || ""} on={v=>set("zoom_meeting_id",v)}/>
              <Field label="Passcode" v={f.zoom_passcode || ""} on={v=>set("zoom_passcode",v)}/>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Status</label>
            <select value={f.status} onChange={e=>set("status", e.target.value)} data-testid="live-f-status" className="w-full rounded-lg border border-slate-300 px-3 py-2">
              {Object.entries(STATUS_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Admin notes</label>
            <textarea rows={2} value={f.admin_notes || ""} onChange={e=>set("admin_notes", e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
          </div>
        </div>
        <button onClick={save} disabled={busy} data-testid="live-save" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#4A6FA5] text-white font-semibold hover:bg-[#3B597B] disabled:opacity-60">
          <Save className="w-4 h-4"/> {busy ? "Saving…" : (isEdit ? "Save changes" : "Create session")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, on, testid }) {
  return <div><label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
    <input value={v} onChange={e=>on(e.target.value)} data-testid={testid} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/></div>;
}
function NumField({ label, v, on, testid }) {
  return <div><label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
    <input type="number" value={v} onChange={e=>on(parseInt(e.target.value||"0",10))} data-testid={testid} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/></div>;
}

function RosterDrawer({ session, data, onClose, onSendReminder, refresh }) {
  const [busy, setBusy] = useState(false);
  const bulk = async (status) => {
    if (!data) return;
    const active = data.registrations.filter(r => r.status === "registered");
    if (!active.length) { toast.info("No registrations awaiting attendance."); return; }
    if (!window.confirm(`Mark ${active.length} learner(s) as ${status}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/portal/live-sessions/${session.id}/attendance`, {
        entries: active.map(r => ({ registration_id: r.id, status })),
      });
      toast.success(`${active.length} marked ${status}.`);
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const mark = async (r, status) => {
    try { await api.post(`/admin/portal/live-sessions/${session.id}/attendance`, { entries: [{ registration_id: r.id, status }] });
      toast.success(`Marked ${status}`); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="live-roster-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose}/>
      <div className="w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Roster</p>
            <h3 className="font-heading text-xl text-slate-900">{session.title}</h3>
            <p className="text-xs text-slate-500">{session.session_date} · {session.start_time}–{session.end_time} {session.time_zone}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        {!data ? <p className="text-sm text-slate-500">Loading…</p> : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
              {Object.entries(data.totals).map(([k,v]) => (
                <div key={k} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{k.replace(/_/g," ")}</div>
                  <div className="font-heading text-lg text-slate-900">{v}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mb-3">
              <button disabled={busy} onClick={() => bulk("attended")} data-testid="live-bulk-attended" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800"><CheckCheck className="w-3 h-3"/> MARK SELECTED ATTENDED</button>
              <button onClick={onSendReminder} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs hover:border-[#4A6FA5]"><Send className="w-3 h-3"/> Send 24-hour reminder</button>
            </div>
            <ul className="space-y-2">
              {data.registrations.map(r => (
                <li key={r.id} data-testid={`live-reg-${r.id}`} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-900 font-medium">{r.learner.first_name} {r.learner.last_name}</p>
                      <p className="text-xs text-slate-500">{r.learner.email} · registered {new Date(r.registered_at).toLocaleString()}</p>
                      <p className="text-[10px] text-slate-400 mt-1">Course status: {r.course_completion_status}</p>
                    </div>
                    <span className="inline-flex text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{r.status}</span>
                  </div>
                  {r.status !== "cancelled" && (
                    <div className="mt-2 flex gap-1.5 text-[11px]">
                      <button onClick={() => mark(r, "attended")} className="px-2 py-1 rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50">Attended</button>
                      <button onClick={() => mark(r, "absent")} className="px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50">Absent</button>
                      <button onClick={() => mark(r, "excused")} className="px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">Excused</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
